export type ApiSuccess<T> = { success: true; data: T }
export type ApiError = { success: false; error: string; code?: string; errors?: Record<string, string[]> }
export type ApiResult<T> = ApiSuccess<T> | ApiError

class ApiRequestError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        public readonly errors?: Record<string, string[]>,
    ) {
        super(message)
    }
}

const request = async <T>(
    path: string,
    options?: RequestInit,
): Promise<T> => {
    const res = await fetch(`/api${path}`, {
        headers: {
            "Content-Type": "application/json",
            ...options?.headers,
        },
        credentials: "include", // for httpOnly cookies (admin auth)
        ...options,
    })

    const json: ApiResult<T> = await res.json()

    if (!json.success) {
        throw new ApiRequestError(json.error, res.status, json.code, json.errors)
    }

    return json.data
}

export const api = {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body: unknown) =>
        request<T>(path, { method: "POST", body: JSON.stringify(body) }),
    patch: <T>(path: string, body: unknown) =>
        request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
    put: <T>(path: string, body: unknown) =>
        request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
    delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
    upload: <T>(path: string, formData: FormData) =>
        request<T>(path, { method: "POST", body: formData, headers: {} }), // no Content-Type for multipart
}

export { ApiRequestError }
